/** Values set at deploy time by src/env.js on window.__env. */
export interface Environment {
  [key: string]: string | number | boolean | undefined;
}

declare global {
  interface Window {
    __env?: Partial<Environment>;
  }
}

export class EnvService {
  public ApiUrl: string;
  public MerchantURL: string;
  public DocsURL: string;
  public CareerURL: string;
  public appIOSURL: string;
  public appAndroidURL: string;
  public applePayMerchantUrl: string;
  public merchantIdentifier: string;
  public enableDebug: boolean;
  public apiKey: string;
  public cneAappIOSURL: string;
  public cneAappAndroidURL: string;
  public showApplePay: boolean;
  public showAddCard: boolean;
  public showSavedCards: boolean;
  public isCnetValidateOnLogin: boolean;
  public GoogleAnalyticsId: string;
  public ClarityProjectId: string;
  public browserBasicAuth: string;
  public merchantBasicAuth: string;

  constructor() {
    const env: Environment = window.__env || {};

    this.ApiUrl = String(env['ApiUrl'] ?? 'https://api.dev-taly.io');
    this.MerchantURL = String(env['MerchantURL'] ?? 'https://dev-taly.io/partners/auth/login');
    this.DocsURL = String(env['DocsURL'] ?? 'https://docs.taly.io/');
    this.CareerURL = String(env['CareerURL'] ?? 'https://boards.eu.greenhouse.io/taly');
    this.appIOSURL = 'https://apps.apple.com/kw/app/taly-split-your-payments/id6467709774';
    this.appAndroidURL = 'https://play.google.com/store/apps/details?id=io.taly.consumer';

    this.cneAappIOSURL = 'https://apps.apple.com/sg/app/kuwait-mobile-id/id1449712307';

    this.cneAappAndroidURL =
      'https://play.google.com/store/apps/details?id=kw.gov.paci.PACIMobileID';

    this.applePayMerchantUrl = String(
      env['applePayMerchantUrl'] ?? 'https://dev-taly.io/apple-pay-session'
    );
    this.merchantIdentifier = String(env['merchantIdentifier'] ?? 'merchant.io.taly');
    this.enableDebug = Boolean(env['enableDebug'] ?? true);
    this.apiKey = String(env['apiKey'] ?? '');
    this.showApplePay = this.toBoolean(env['showApplePay'] ?? true);
    this.showAddCard = this.toBoolean(env['showAddCard'] ?? false);
    this.showSavedCards = this.toBoolean(env['showSavedCards'] ?? false);
    this.isCnetValidateOnLogin = this.toBoolean(env['isCnetValidateOnLogin'] ?? true);
    this.GoogleAnalyticsId = String(env['GoogleAnalyticsId'] ?? '');
    this.ClarityProjectId = String(env['ClarityProjectId'] ?? '');
    this.browserBasicAuth = this.toBasicAuthHeader(env['browserBasicAuth']);
    this.merchantBasicAuth = this.toBasicAuthHeader(env['merchantBasicAuth']);
  }

  // Accept the token with or without a leading "Basic " so the header is never prefixed twice
  private toBasicAuthHeader(value: string | number | boolean | undefined): string {
    const token = String(value ?? '')
      .trim()
      .replace(/^Basic\s+/i, '');
    return `Basic ${token}`;
  }

  private toBoolean(value: string | number | boolean): boolean {
    if (typeof value === 'string') {
      return value.toLowerCase() === 'true';
    }
    if (typeof value === 'number') {
      return value !== 0; // 0 = false, any other number = true
    }
    return value; // already boolean
  }

  onLinkClick(event: MouseEvent | KeyboardEvent, appLink: string): void {
    if (!appLink || appLink.trim() === '') {
      event.preventDefault();
      return;
    }

    // Allow keyboard activation only for Enter or Space
    if (event instanceof KeyboardEvent && event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    window.open(appLink, '_blank');
  }
}
